import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Phone, Eye, EyeOff, Facebook, Twitter, Instagram, Sparkles, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import './Auth.css';

const GoogleIcon = () => (
  <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export const AuthPage = ({ initialMode = 'signin' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signin, signup, signInWithGoogle, resetPassword } = useAuth();

  const [isSignIn, setIsSignIn] = useState(initialMode !== 'signup');
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    organization: '',
    password: '',
    confirmPassword: '',
    accountType: 'guest' // guest, owner, or manager
  });

  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirm, setShowSignupConfirm] = useState(false);

  useEffect(() => {
    setIsSignIn(initialMode !== 'signup');
  }, [initialMode]);

  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const from = useMemo(() => location.state?.from?.pathname || '/dashboard', [location.state]);
  const isSEECSAdmin = signupData.email.trim().toLowerCase().endsWith('@seecs.edu.pk');

  const containerClass = ['auth-portal', isReady ? 'is-ready' : '', isSignIn ? 'sign-in' : 'sign-up']
    .filter(Boolean)
    .join(' ');

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSignupChange = (e) => {
    const { name, value } = e.target;
    setSignupData((prev) => ({ ...prev, [name]: value }));
  };

  const validateSignup = () => {
    // Validate full name
    const trimmedName = signupData.name.trim();
    if (!trimmedName) {
      toast.error('Please enter your full name');
      return false;
    }
    if (trimmedName.length < 2) {
      toast.error('Full name must be at least 2 characters long');
      return false;
    }
    if (trimmedName.length > 100) {
      toast.error('Full name must not exceed 100 characters');
      return false;
    }
    
    // Validate email
    if (!signupData.email.trim()) {
      toast.error('Please enter a valid email');
      return false;
    }
    if (signupData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return false;
    }
    // Check password complexity
    if (!/(?=.*[a-z])/.test(signupData.password)) {
      toast.error('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/(?=.*[A-Z])/.test(signupData.password)) {
      toast.error('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/(?=.*\d)/.test(signupData.password)) {
      toast.error('Password must contain at least one number');
      return false;
    }
    if (signupData.password !== signupData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };



  const handleSignIn = async (event) => {
    event.preventDefault();

    if (!loginData.email || !loginData.password) {
      toast.error('Please fill in both fields');
      return;
    }

    setIsLoading(true);
    try {
      const result = await signin(loginData.email, loginData.password);
      
      // Wait a moment for AuthContext to finish loading hotel status for owners
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Role-based redirect
      const role = result?.role || result?.user?.role;
      
      switch(role) {
        case 'admin':
          navigate('/admin', { replace: true });
          break;
        case 'manager':
          navigate('/manager', { replace: true });
          break;
        case 'owner':
          navigate('/owner', { replace: true });
          break;
        case 'guest':
        default:
          navigate('/guest/dashboard', { replace: true });
          break;
      }
    } catch (error) {
      // handled in context
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (event) => {
    event.preventDefault();

    if (!validateSignup()) {
      return;
    }

    setIsLoading(true);
    try {
      const role = isSEECSAdmin ? 'admin' : (signupData.accountType === 'owner' ? 'owner' : (signupData.accountType === 'manager' ? 'manager' : 'guest'));
      const organization = isSEECSAdmin ? 'SEECS, NUST' : signupData.organization || null;

      // Ensure date is in YYYY-MM-DD format if provided
      let formattedDateOfBirth = null;
      if (signupData.dateOfBirth) {
        const date = new Date(signupData.dateOfBirth);
        if (!isNaN(date.getTime())) {
          // Format as YYYY-MM-DD
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          formattedDateOfBirth = `${year}-${month}-${day}`;
        }
      }

      if (signupData.accountType === 'manager') {
        // Manager signup: call manager-signup API directly
        const res = await fetch('/api/auth/manager-signup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            full_name: signupData.name,
            email: signupData.email,
            password: signupData.password,
            phone: signupData.phone || null
          })
        });
        const result = await res.json();
        if (res.ok) {
          toast.success('Manager account created! Logging you in...');
          try {
            // Auto-login manager after signup
            const user = await signin(signupData.email, signupData.password);
            if (user?.role === 'manager') {
              navigate('/manager', { replace: true });
            } else {
              navigate('/guest/dashboard', { replace: true });
            }
          } catch (loginError) {
            toast.error('Signup succeeded but auto-login failed. Please sign in manually.');
            setIsSignIn(true);
          }
        } else {
          toast.error(result.message || 'Manager signup failed.');
        }
        setIsLoading(false);
        return;
      }

      const additionalData = {
        fullName: signupData.name,
        phone: signupData.phone || null,
        dateOfBirth: formattedDateOfBirth,
        role,
        organization
      };

      const user = await signup(signupData.email, signupData.password, additionalData);

      // Wait a moment for AuthContext to finish setting up user profile
      await new Promise(resolve => setTimeout(resolve, 500));

      // After signup, redirect based on role
      if (role === 'owner') {
        // Owners must complete hotel registration
        navigate('/owner/register', { replace: true });
        toast.info('Please complete your hotel registration to continue.');
      } else if (role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        // Redirect guests to the new dashboard with persistent layout
        navigate('/guest/dashboard', { replace: true });
      }
    } catch (error) {
      // handled in context
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!loginData.email) {
      toast.error('Please enter your email address first');
      return;
    }

    try {
      await resetPassword(loginData.email);
    } catch (error) {
      // handled in context
    }
  };

  const handleGoogleAuth = async () => {
    setIsLoading(true);
    try {
      await signInWithGoogle();
      navigate(from, { replace: true });
    } catch (error) {
      // handled in context
    } finally {
      setIsLoading(false);
    }
  };

  const handleModeSwitch = (mode) => {
    setIsSignIn(mode === 'signin');
  };

  return (
    <div className="auth-viewport">
      <div className={containerClass}>
        <div className="row">
          <div className="col align-items-center flex-col sign-up">
            <div className="form-wrapper align-items-center">
              <form className="form sign-up" onSubmit={handleSignUp}>
                <h3>Create Account</h3>
                <p className="caption">Join InnBotics and unlock smart hospitality tools.</p>

                <div className="input-group" style={{ marginBottom: '1.5rem' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '0.5rem', 
                    fontWeight: '600',
                    color: '#333',
                    fontSize: '0.95rem'
                  }}>
                    I am registering as:
                  </label>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <label style={{
                      flex: 1,
                      padding: '1rem',
                      border: signupData.accountType === 'guest' ? '2px solid #6366f1' : '2px solid #e5e7eb',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      background: signupData.accountType === 'guest' ? '#eff6ff' : 'white'
                    }}>
                      <input
                        type="radio"
                        name="accountType"
                        value="guest"
                        checked={signupData.accountType === 'guest'}
                        onChange={handleSignupChange}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <span style={{ fontWeight: '500' }}>Guest</span>
                      <p style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        margin: '0.25rem 0 0 0',
                        textAlign: 'center',
                        width: '100%'
                      }}>
                        Book hotels and manage reservations
                      </p>
                    </label>
                    <label style={{
                      flex: 1,
                      padding: '1rem',
                      border: signupData.accountType === 'owner' ? '2px solid #6366f1' : '2px solid #e5e7eb',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      background: signupData.accountType === 'owner' ? '#eff6ff' : 'white'
                    }}>
                      <input
                        type="radio"
                        name="accountType"
                        value="owner"
                        checked={signupData.accountType === 'owner'}
                        onChange={handleSignupChange}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <span style={{ fontWeight: '500' }}>Hotel Owner</span>
                      <p style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        margin: '0.25rem 0 0 0',
                        textAlign: 'center',
                        width: '100%'
                      }}>
                        List and manage your property
                      </p>
                    </label>
                    <label style={{
                      flex: 1,
                      padding: '1rem',
                      border: signupData.accountType === 'manager' ? '2px solid #6366f1' : '2px solid #e5e7eb',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      background: signupData.accountType === 'manager' ? '#eff6ff' : 'white'
                    }}>
                      <input
                        type="radio"
                        name="accountType"
                        value="manager"
                        checked={signupData.accountType === 'manager'}
                        onChange={handleSignupChange}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <span style={{ fontWeight: '500' }}>Manager</span>
                      <p style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        margin: '0.25rem 0 0 0',
                        textAlign: 'center',
                        width: '100%'
                      }}>
                        Sign up as a hotel manager (invitation required)
                      </p>
                    </label>
                  </div>
                </div>

                <div className="input-group">
                  <User className="auth-icon" />
                  <input
                    type="text"
                    name="name"
                    placeholder="Full name"
                    value={signupData.name}
                    onChange={handleSignupChange}
                    autoComplete="name"
                    required
                  />
                </div>

                <div className="input-group">
                  <Mail className="auth-icon" />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email address"
                    value={signupData.email}
                    onChange={handleSignupChange}
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="input-group">
                  <Phone className="auth-icon" />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone number (optional)"
                    value={signupData.phone}
                    onChange={handleSignupChange}
                    autoComplete="tel"
                  />
                </div>

                <div className="input-group">
                  <User className="auth-icon" />
                  <input
                    type="date"
                    name="dateOfBirth"
                    placeholder="Date of Birth (optional)"
                    value={signupData.dateOfBirth}
                    onChange={handleSignupChange}
                    autoComplete="bday"
                  />
                </div>

                <div className="input-group">
                  <Lock className="auth-icon" />
                  <input
                    type={showSignupPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Create password"
                    value={signupData.password}
                    onChange={handleSignupChange}
                    autoComplete="new-password"
                    minLength={6}
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowSignupPassword((prev) => !prev)}
                    aria-label={showSignupPassword ? 'Hide password' : 'Show password'}
                  >
                    {showSignupPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                <p className="password-hint" style={{ fontSize: '12px', color: '#666', marginTop: '-8px', marginBottom: '12px' }}>
                  Must be 6+ characters with uppercase, lowercase, and a number
                </p>

                <div className="input-group">
                  <Lock className="auth-icon" />
                  <input
                    type={showSignupConfirm ? 'text' : 'password'}
                    name="confirmPassword"
                    placeholder="Confirm password"
                    value={signupData.confirmPassword}
                    onChange={handleSignupChange}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowSignupConfirm((prev) => !prev)}
                    aria-label={showSignupConfirm ? 'Hide password' : 'Show password'}
                  >
                    {showSignupConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {isSEECSAdmin && (
                  <div className="demo-note">
                    <strong>SEECS email detected</strong>
                    Admin privileges will be assigned automatically.
                  </div>
                )}

                <button type="submit" className="primary" disabled={isLoading}>
                  {isLoading ? 'Creating account...' : 'Sign up'}
                </button>

                <p className="alt-action">
                  Already have an account?
                  <button type="button" onClick={() => handleModeSwitch('signin')}>
                    Sign in here
                  </button>
                </p>

                {isLoading && isSignIn === false && (
                  <span className="status-text">
                    <span className="spinner" />
                    Working on it...
                  </span>
                )}
              </form>
            </div>

            <div className="social-list sign-up">
              <button
                type="button"
                className="social-item google-bg"
                onClick={handleGoogleAuth}
                disabled={isLoading}
                aria-label="Continue with Google"
              >
                <GoogleIcon />
              </button>
              <button type="button" className="social-item facebook-bg" disabled aria-label="Facebook login coming soon">
                <Facebook size={20} />
              </button>
              <button type="button" className="social-item twitter-bg" disabled aria-label="Twitter login coming soon">
                <Twitter size={20} />
              </button>
              <button type="button" className="social-item insta-bg" disabled aria-label="Instagram login coming soon">
                <Instagram size={20} />
              </button>
            </div>
          </div>

          <div className="col align-items-center flex-col sign-in">
            <div className="form-wrapper align-items-center">
              <form className="form sign-in" onSubmit={handleSignIn}>
                <h3>Welcome back</h3>
                <p className="caption">Sign in to manage bookings, concierge and IoT services.</p>

                <div className="input-group">
                  <Mail className="auth-icon" />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email address"
                    value={loginData.email}
                    onChange={handleLoginChange}
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="input-group">
                  <Lock className="auth-icon" />
                  <input
                    type={showLoginPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Password"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowLoginPassword((prev) => !prev)}
                    aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                  >
                    {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                <button type="button" className="forgot-link" onClick={handleForgotPassword}>
                  Forgot password?
                </button>

                <button type="submit" className="primary" disabled={isLoading}>
                  {isLoading ? 'Signing you in...' : 'Sign in'}
                </button>

                <p className="alt-action">
                  New to InnBotics?
                  <button type="button" onClick={() => handleModeSwitch('signup')}>
                    Create an account
                  </button>
                </p>

                {isLoading && isSignIn === true && (
                  <span className="status-text">
                    <span className="spinner" />
                    Authenticating...
                  </span>
                )}
              </form>
            </div>

            <div className="social-list sign-in">
              <button
                type="button"
                className="social-item google-bg"
                onClick={handleGoogleAuth}
                disabled={isLoading}
                aria-label="Continue with Google"
              >
                <GoogleIcon />
              </button>
              <button type="button" className="social-item facebook-bg" disabled aria-label="Facebook login coming soon">
                <Facebook size={20} />
              </button>
              <button type="button" className="social-item twitter-bg" disabled aria-label="Twitter login coming soon">
                <Twitter size={20} />
              </button>
              <button type="button" className="social-item insta-bg" disabled aria-label="Instagram login coming soon">
                <Instagram size={20} />
              </button>
            </div>
          </div>
        </div>

        <div className="row content-row">
          <div className="col align-items-center flex-col">
            <div className="text sign-in">
              <span className="welcome-chip">
                <Sparkles size={16} />
                Welcome Back
              </span>
              <h2>
                <span className="gradient-word">InnBotics</span> Command Suite
              </h2>
              <p>Pick up right where your team left off with instant access to bookings, concierge threads, and live device telemetry.</p>
              <p className="subline">Keep every guest journey orchestrated through one beautiful, AI-augmented workspace.</p>
            </div>
            <div className="illustration sign-in">Live Operations Pulse</div>
          </div>

          <div className="col align-items-center flex-col">
            <div className="illustration sign-up">AI Concierge Canvas</div>
            <div className="text sign-up">
              <span className="welcome-chip">
                <ArrowRight size={16} />
                New to InnBotics
              </span>
              <h2>
                Launch the <span className="gradient-word">Future Stay</span>
              </h2>
              <p>Spin up conversational bookings, digital keys, and predictive operations the moment you join our platform.</p>
              <p className="subline">Join a growing collective of properties designing unforgettable arrivals with automation.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Login = () => <AuthPage initialMode="signin" />;

export default Login;